import { useCallback, useEffect, useState } from 'react';
import { FileText, Loader2, Plus, Wallet } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';
import NotaExplicativa from '../ui/NotaExplicativa';
import { pesos } from './dinero';

/* =====================================================================
   Cuentas por pagar · ERP-OPERATIVO EF1 y EF2
   =====================================================================
   Una pantalla, tres cosas: capturar la factura de un proveedor, ver
   cuánto se debe y desde cuándo (la antigüedad, copiada de la de
   clientes), y pagar. Un pago es un hecho: no se edita, se compensa con
   otro en negativo y su motivo. Si la empresa encendió la regla «Pagar a
   un proveedor», un pago grande espera firma y la pantalla lo dice.
   ===================================================================== */
export default function CuentasPorPagar() {
  const { puede } = useAuth();
  const avisos = useAvisos();
  const [datos, setDatos] = useState(null);
  const [antiguedad, setAntiguedad] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [error, setError] = useState('');
  const [capturando, setCapturando] = useState(false);
  const [pagando, setPagando] = useState(null);

  // Se recarga subiendo un contador: el efecto lee y guarda cuando llega
  // la respuesta, nunca de forma síncrona (regla del compilador de React).
  const [tick, setTick] = useState(0);
  const cargar = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    let vivo = true;
    Promise.all([
      api.get('/cxp/cuentas'),
      api.get('/cxp/informes/antiguedad'),
      api.get('/compras/proveedores').catch(() => []),
    ]).then(([c, a, p]) => {
      if (!vivo) return;
      setDatos(c); setAntiguedad(a); setProveedores(Array.isArray(p) ? p : []);
      setError('');
    }).catch((e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [tick]);

  if (error && !datos) return <p className="p-4 text-sm text-red-700">{error}</p>;
  if (!datos) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Sumando lo que se debe…
      </p>
    );
  }

  return (
    <div className="flex max-w-5xl flex-col gap-5 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Cuentas por pagar</h1>
        <p className="text-gray-600">Lo que le debes a tus proveedores, factura por factura, y desde cuándo.</p>
        <Ayuda id="finanzas.cxp.pantalla" />
      </header>

      <div className="grid gap-3 sm:grid-cols-3">
        <Cifra etiqueta="Por pagar" valor={datos.total} tono="text-gray-900" />
        <Cifra etiqueta="Ya vencido" valor={datos.vencido} tono={Number(datos.vencido) > 0 ? 'text-red-700' : 'text-gray-900'} />
        <Cifra etiqueta="Facturas con saldo" valor={datos.documentos} plano />
      </div>
      <p className="text-xs text-gray-500">Dato al {datos.dato_al}.</p>

      {puede('cxp.capturar') && (
        <button type="button" onClick={() => setCapturando((v) => !v)}
                className="inline-flex w-fit items-center gap-2 rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold text-white">
          <Plus className="h-4 w-4" aria-hidden="true" /> Capturar factura de proveedor
        </button>
      )}
      {capturando && (
        <FormularioFactura proveedores={proveedores}
                           onHecho={(m) => { avisos.exito(m); setCapturando(false); cargar(); }} />
      )}

      <section className="rounded-xl border border-gray-200">
        <h2 className="border-b border-gray-200 px-3 py-2 font-semibold text-gray-800">
          Facturas con saldo ({datos.cuentas.length})
        </h2>
        {datos.cuentas.length === 0 ? (
          <NotaExplicativa titulo="No debes nada a proveedores">
            Cuando captures la factura de un proveedor, aparecerá aquí con su vencimiento y su saldo.
          </NotaExplicativa>
        ) : (
          <ul className="divide-y divide-gray-100">
            {datos.cuentas.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-x-3 px-3 py-2 text-sm">
                <FileText className="h-4 w-4 text-gray-400" aria-hidden="true" />
                <span className="font-medium text-gray-900">{c.proveedor}</span>
                <span className="text-gray-500">{c.folio_proveedor || `factura ${c.id_factura_recibida}`}{c.concepto ? ` · ${c.concepto}` : ''}</span>
                <span className={c.dias_vencida > 0 ? 'text-red-700' : 'text-gray-600'}>
                  {c.dias_vencida > 0 ? `vencida hace ${c.dias_vencida} día(s)` : `vence ${c.fecha_vence}`}
                </span>
                <span className="ml-auto tabular-nums font-semibold">{pesos(c.saldo)}</span>
                {puede('cxp.pagar') && (
                  <button type="button" onClick={() => setPagando(pagando?.id === c.id ? null : c)}
                          className="inline-flex items-center gap-1 font-semibold text-[var(--color-marca)]">
                    <Wallet className="h-3 w-3" aria-hidden="true" /> Pagar
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {pagando && (
        <FormularioPago cuenta={pagando}
                        onHecho={(m) => { avisos.exito(m); setPagando(null); cargar(); }} />
      )}

      {antiguedad.length > 0 && (
        <section className="rounded-xl border border-gray-200">
          <h2 className="border-b border-gray-200 px-3 py-2 font-semibold text-gray-800">
            Antigüedad por proveedor
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Proveedor</th>
                  <th className="px-3 py-2 text-right">Por vencer</th>
                  <th className="px-3 py-2 text-right">1–30</th>
                  <th className="px-3 py-2 text-right">31–60</th>
                  <th className="px-3 py-2 text-right">61–90</th>
                  <th className="px-3 py-2 text-right">+90</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {antiguedad.map((f) => (
                  <tr key={f.id_proveedor} className="border-t border-gray-100">
                    <td className="px-3 py-2">{f.proveedor}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pesos(f.por_vencer)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pesos(f.de_1_a_30)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pesos(f.de_31_a_60)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pesos(f.de_61_a_90)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-red-700">{pesos(f.mas_de_90)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{pesos(f.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function Cifra({ etiqueta, valor, tono, plano }) {
  return (
    <div className="rounded-xl border border-gray-200 p-3">
      <div className={`text-2xl font-semibold tabular-nums ${tono || 'text-gray-900'}`}>
        {plano ? valor : pesos(valor)}
      </div>
      <div className="text-sm text-gray-600">{etiqueta}</div>
    </div>
  );
}

function FormularioFactura({ proveedores, onHecho }) {
  const hoy = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ id_proveedor: proveedores[0]?.id || '', folio_proveedor: '',
                               fecha: hoy, fecha_vence: hoy, subtotal: '', iva: '', concepto: '' });
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const total = (Number(f.subtotal || 0) + Number(f.iva || 0)).toFixed(2);
  const listo = f.id_proveedor && Number(total) > 0 && f.fecha_vence >= f.fecha;

  async function guardar(e) {
    e.preventDefault();
    setOcupado(true); setError('');
    try {
      const r = await api.post('/cxp/facturas', {
        id_proveedor: Number(f.id_proveedor), folio_proveedor: f.folio_proveedor || null,
        fecha: f.fecha, fecha_vence: f.fecha_vence,
        subtotal: Number(f.subtotal || 0).toFixed(2), iva: Number(f.iva || 0).toFixed(2),
        total, concepto: f.concepto || null,
      });
      onHecho(r.mensaje);
    } catch (err) { setError(err.message); } finally { setOcupado(false); }
  }

  return (
    <form onSubmit={guardar} className="grid gap-3 rounded-xl border border-gray-200 p-4 sm:grid-cols-2">
      <label className="flex flex-col text-sm">
        Proveedor *
        <select value={f.id_proveedor} onChange={(e) => setF({ ...f, id_proveedor: e.target.value })}
                className="rounded-lg border border-gray-300 p-2">
          {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>
      </label>
      <label className="flex flex-col text-sm">
        Folio del proveedor
        <input value={f.folio_proveedor} onChange={(e) => setF({ ...f, folio_proveedor: e.target.value })}
               className="rounded-lg border border-gray-300 p-2" placeholder="Como viene en su factura" />
      </label>
      <label className="flex flex-col text-sm">
        Fecha de la factura
        <input type="date" value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })}
               className="rounded-lg border border-gray-300 p-2" />
      </label>
      <label className="flex flex-col text-sm">
        Vence *
        <input type="date" value={f.fecha_vence} onChange={(e) => setF({ ...f, fecha_vence: e.target.value })}
               className="rounded-lg border border-gray-300 p-2" />
      </label>
      <label className="flex flex-col text-sm">
        Subtotal *
        <input type="number" step="0.01" min="0" value={f.subtotal}
               onChange={(e) => setF({ ...f, subtotal: e.target.value })}
               className="rounded-lg border border-gray-300 p-2" />
      </label>
      <label className="flex flex-col text-sm">
        IVA
        <input type="number" step="0.01" min="0" value={f.iva}
               onChange={(e) => setF({ ...f, iva: e.target.value })}
               className="rounded-lg border border-gray-300 p-2" />
      </label>
      <label className="flex flex-col text-sm sm:col-span-2">
        Concepto
        <input value={f.concepto} onChange={(e) => setF({ ...f, concepto: e.target.value })}
               className="rounded-lg border border-gray-300 p-2" placeholder="Qué compraste" />
      </label>
      <p className="text-sm text-gray-700 sm:col-span-2">Total: <strong>{pesos(total)}</strong></p>
      {error && <p className="text-sm text-red-700 sm:col-span-2">{error}</p>}
      <div className="flex items-center gap-3 sm:col-span-2">
        <button type="submit" disabled={!listo || ocupado}
                className="rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold text-white disabled:bg-gray-300">
          Guardar factura
        </button>
        {!listo && <span className="text-xs text-amber-700">Hace falta proveedor, un total mayor que cero y una fecha de vencimiento igual o posterior a la de la factura.</span>}
      </div>
    </form>
  );
}

function FormularioPago({ cuenta, onHecho }) {
  const [f, setF] = useState({ importe: String(cuenta.saldo), metodo: 'TRANSFERENCIA', referencia: '', motivo: '' });
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const negativo = Number(f.importe) < 0;
  const listo = Number(f.importe) !== 0 && (!negativo || f.motivo.trim());

  async function pagar(e) {
    e.preventDefault();
    setOcupado(true); setError('');
    try {
      const r = await api.post('/cxp/pagos', {
        id_cuenta: cuenta.id, importe: Number(f.importe).toFixed(2), metodo: f.metodo,
        referencia: f.referencia || null, motivo: f.motivo || null,
        clave_idempotencia: crypto.randomUUID(),
      });
      onHecho(r.mensaje);
    } catch (err) { setError(err.message); } finally { setOcupado(false); }
  }

  return (
    <form onSubmit={pagar} className="grid gap-3 rounded-xl border border-gray-200 p-4 sm:grid-cols-2">
      <p className="text-sm text-gray-700 sm:col-span-2">
        Pagar a <strong>{cuenta.proveedor}</strong> · saldo {pesos(cuenta.saldo)}
      </p>
      <label className="flex flex-col text-sm">
        Importe *
        <input type="number" step="0.01" value={f.importe} onChange={(e) => setF({ ...f, importe: e.target.value })}
               className="rounded-lg border border-gray-300 p-2" />
      </label>
      <label className="flex flex-col text-sm">
        Método
        <select value={f.metodo} onChange={(e) => setF({ ...f, metodo: e.target.value })}
                className="rounded-lg border border-gray-300 p-2">
          {['TRANSFERENCIA', 'EFECTIVO', 'CHEQUE', 'TARJETA', 'OTRO'].map((m) => <option key={m}>{m}</option>)}
        </select>
      </label>
      <label className="flex flex-col text-sm">
        Referencia
        <input value={f.referencia} onChange={(e) => setF({ ...f, referencia: e.target.value })}
               className="rounded-lg border border-gray-300 p-2" placeholder="Clave del SPEI, número de cheque…" />
      </label>
      {negativo && (
        <label className="flex flex-col text-sm">
          Motivo del pago en negativo *
          <input value={f.motivo} onChange={(e) => setF({ ...f, motivo: e.target.value })}
                 className="rounded-lg border border-gray-300 p-2" />
        </label>
      )}
      <div className="sm:col-span-2">
        <Ayuda id="finanzas.cxp.pago_negativo" />
        <Ayuda id="finanzas.cxp.umbral" />
      </div>
      {error && <p className="text-sm text-red-700 sm:col-span-2">{error}</p>}
      <button type="submit" disabled={!listo || ocupado}
              className="w-fit rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold text-white disabled:bg-gray-300">
        Registrar pago
      </button>
    </form>
  );
}
